/**
 * Citation Display Utilities
 *
 * Shared utility functions for citation display components. Used by
 * InlineCitation, UniversalCitationCard, and other citation UI components.
 */

import { bbox, bboxPolygon } from '@turf/turf';
import type { AllGeoJSON } from '@turf/helpers';
import { getExternalHttpUrl } from '../../utils/urlHelpers';

/** Source type display labels for human-readable source type names */
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  FILES_RAG: 'File',
  WEB: 'Web',
  INCIDENT: 'Incident',
  EXERCISE: 'Exercise',
  SITREP: 'SITREP',
  NEO: 'NEO Analysis',
  CUSTOM: 'Custom Data',
};

/**
 * Clean up display name by removing redundant prefixes
 *
 * Removes prefixes like "Exercise:" or "Custom Data:" that are redundant when
 * the source type badge is already showing the source type.
 *
 * @param name - The source name to clean
 * @param sourceType - Optional source type for context-aware cleaning
 * @returns Cleaned display name
 */
export function cleanDisplayName(name: string, sourceType?: string): string {
  if (!name) return 'Unknown';

  // Remove "Exercise:" prefix for EXERCISE sources
  if (sourceType === 'EXERCISE' && name.startsWith('Exercise:')) {
    return name.replace(/^Exercise:\s*/, '').trim() || 'Exercise';
  }

  // Remove "Custom Data:" prefix for CUSTOM sources
  if (sourceType === 'CUSTOM' && name.startsWith('Custom Data:')) {
    return name.replace(/^Custom Data:\s*/, '').trim() || 'Custom Data';
  }

  return name;
}

/**
 * Truncate a display name to a maximum length with ellipsis
 *
 * @param name - The name to truncate
 * @param maxLength - Maximum length (default: 60)
 * @returns Truncated name with ellipsis if needed
 */
export function truncateDisplayName(name: string, maxLength = 60): string {
  if (!name || name.length <= maxLength) return name;
  return `${name.substring(0, maxLength - 3)}...`;
}

/**
 * Get human-readable source type label
 *
 * @param sourceType - The source type constant (e.g., 'FILES_RAG')
 * @returns Human-readable label (e.g., 'File')
 */
export function getSourceTypeLabel(sourceType: string): string {
  return SOURCE_TYPE_LABELS[sourceType] || sourceType;
}

/**
 * CustomSourceId the COMS map-layer tools stamp on their citation metadata (see
 * DGPT/layers/layerSource.ts).
 */
export const MAP_LAYERS_CUSTOM_SOURCE_ID = 'map_layers';

/**
 * True when a CUSTOM citation source came from the map-layer tools and can be
 * flown to on the map.
 */
export function isMapLayerSource(source: {
  sourceType: string;
  metadata?: { customSourceId?: string };
}): boolean {
  return (
    source.sourceType === 'CUSTOM' &&
    source.metadata?.customSourceId === MAP_LAYERS_CUSTOM_SOURCE_ID
  );
}

/**
 * Human-viewable page for a CUSTOM citation source (census profile, USGS event
 * page, …). Clicking the citation opens it in a new tab. Only http(s) URLs are
 * honored.
 */
export function getCustomSourceUrl(source: {
  metadata?: { url?: string };
}): string | null {
  return getExternalHttpUrl(source.metadata?.url);
}

type GeoJsonGeometry = { type: string; coordinates: unknown };

function isGeometry(value: unknown): value is GeoJsonGeometry {
  const candidate = value as GeoJsonGeometry | null | undefined;
  return Boolean(
    candidate &&
      typeof candidate.type === 'string' &&
      candidate.coordinates !== undefined
  );
}

/**
 * Frame a set of GeoJSON geometries with the smallest geometry that contains
 * them: a Point for a single position, else the envelope Polygon. Client-side
 * mirror of the backend's computeMapFocus (DGPT/layers/layerSource.ts) so
 * payloads generated before `map_focus` existed still resolve to a fly-to
 * target.
 */
function frameGeometries(geometries: unknown[]): GeoJsonGeometry | null {
  const valid = geometries.filter(isGeometry);
  if (valid.length === 0) return null;

  let bounds: number[];
  try {
    bounds = bbox({
      type: 'GeometryCollection',
      geometries: valid,
    } as AllGeoJSON);
  } catch {
    return null;
  }
  const [minLon, minLat, maxLon, maxLat] = bounds;
  // An empty collection yields an infinite bbox.
  if (!bounds.every(Number.isFinite)) return null;

  if (minLon === maxLon && minLat === maxLat) {
    return { type: 'Point', coordinates: [minLon, minLat] };
  }
  return bboxPolygon([minLon, minLat, maxLon, maxLat]).geometry;
}

/**
 * Extract the fly-to GeoJSON geometry from a map-layer citation payload. The
 * backend puts a canonical `map_focus` (Point or envelope Polygon) on every
 * layer tool payload; payloads generated before that field existed fall back to
 * framing whatever geometry summaries they carry (per-layer bboxes and
 * centroids, feature points, proximity-result points). Returns null when there
 * is nothing to fly to (e.g. an external-layer citation).
 */
export function getMapFocusFromSource(source: {
  originalContent?: string;
}): GeoJsonGeometry | null {
  if (!source.originalContent) return null;
  try {
    const payload = JSON.parse(source.originalContent);
    if (isGeometry(payload.map_focus)) return payload.map_focus;

    return frameGeometries([
      payload.bbox,
      payload.centroid,
      payload.summary?.layer_bbox,
      payload.summary?.layer_centroid,
      ...(Array.isArray(payload.internal_layers)
        ? payload.internal_layers.flatMap(
            (layer: { bbox?: unknown; centroid?: unknown }) => [
              layer.bbox,
              layer.centroid,
            ]
          )
        : []),
      ...(Array.isArray(payload.features)
        ? payload.features.map((feature: { point?: unknown }) => feature.point)
        : []),
      ...(Array.isArray(payload.results)
        ? payload.results.map((result: { point?: unknown }) => result.point)
        : []),
    ]);
  } catch {
    return null;
  }
}
